/**
 * 埋点相关-gif方案实现
 * http://wiki.opple.com/pages/viewpage.action?pageId=62953298
 */
import store from "@/store/index.js";
import moment from "moment";
import qs from "qs";
import jsrsasign from "jsrsasign";
import Bowser from "bowser";
import { isDev, isLocalServer } from "@utils/utils";

const APP_ID = "web"; // 接口区分使用哪个公钥验签
const APP_NAME = "web_opple_commercial_lighting_log"; // 项目内唯一
// const gifUrl = "http://10.0.98.11:8180/iot-operator-tool/log/web.gif";
// RSA密钥对生成工具: https://uutool.cn/rsa-generate/
const privateKey = `-----BEGIN PRIVATE KEY-----
MIIBUwIBADANBgkqhkiG9w0BAQEFAASCAT0wggE5AgEAAkEArwnu+vwEWEbVWs4p7Nva177s+rycucURr6L8lbMibHNqeAP8+Dat3ha+q0gnAYZ6m1aUCvoVtnFr1ZwPfX+aMQIDAQABAkAk3DmoPvQHRvtdAPdROscuEbXD4345nKdpzi/UsoF4Rsg55CJX+v6GPO+mw7+W8pywVPnW/6RiKZ5EL3zFz/3xAiEA6Yqoc9cKiOb3d6VND8breQXw20FR+AblWOe5iki6SI0CIQC/3wyfS0E1Ble5qgFtlPlt5Pr8ShVrnYRi9HS1cpApNQIgKzb0KtNM6L5MZoBY/SVi5zcZNYklwM6N2+CtLZB9n/UCIFX05wK2BBBtNv/GWb1t8BB7dLVxuwbrGlP6kA55tHvBAiARUfgxm/AiLa4EMhkv9/0mNeZ5MNwlmNzcQPHgl3k9PA==
-----END PRIVATE KEY-----`;

const { VUE_APP_gifOrigin } = process.env

/**
 * gif请求地址
 * 后续弄到环境变量去
 */
// const gifOriginMap = {
//   debug3: "https://debug3.opple.com",
//   stage: "https://stage.opple.com",
//   iot1: "https://opsc-qa-public1.opple.com", // 欧洲
//   iot2: "https://opsc-qa-public.opple.com", // 国内
// };

/** 埋点数据类型 */
export const TRACE_DATA_TYPE_ENUM = {
  /** 请求埋点 */
  netEventTrack: "netEventTrack",
  /** window 生命周期埋点 */
  windowLife: "windowLife",
  /** socket 生命周期埋点 */
  socketLife: "socketLife",
  /** socket 实例化埋点 */
  socketCreate: "socketCreate",
  /** socket 注册事件埋点 emit */
  socketSendEvent: "socketSendEvent",
  /** socket 接受数据埋点 on */
  socketOnEvent: "socketOnEvent",
  /** error埋点: 1.代码中可能会发生错误的地方埋点; 2.try...catch埋点 等 */
  error: "error",
  /** info埋点: 协助排查问题的日志埋点, 例如:接口响应数据... */
  info: "info",
  /** performance性能指标埋点 */
  performance: "performance",
  /** 双路传输埋点 */
  shuangluTrans: "shuangluTrans",
};

const DATA_TYPE_MAP = {
  netEventTrack: 1,
  socketLife: 5,
  socketCreate: 5,
  socketSendEvent: 5,
  socketOnEvent: 5,
  error: 7,
  info: 8,
  performance: 9,
  shuangluTrans: 12,
};

/** 获取gif请求地址 */
function getGifUrl() {
  // const envs = Object.keys(gifOriginMap);
  // let gifOrigin = "";
  // let origin = window.location.origin || "";
  // for (let i = 0; i < envs.length; i++) {
  //   if (origin.indexOf(envs[i]) > -1) {
  //     gifOrigin = gifOriginMap[envs[i]];
  //     break;
  //   }
  // }
  // gifOrigin = gifOrigin || gifOriginMap.debug3; // 默认兜底到debug3
  const gifOrigin = window.VUE_APP_gifOrigin || VUE_APP_gifOrigin;
  
  return `${gifOrigin}/iot-operator-tool/log/web.gif`;
}

/**
 * 组装数据, 请求gif
 * @param {*} data
 * @param {string} dataDes 埋点类型
 */
export function Trace(data = {}, dataDes = TRACE_DATA_TYPE_ENUM.netEventTrack) {
  if (typeof window.requestIdleCallback !== "function") return;
  window.requestIdleCallback(
    () => {
      try {
        if (isDev()) return; // 本地开发不收集埋点
        // if (store.state.isInEurope) return; // 欧洲暂不埋点
        if(isLocalServer()) return // 本地服务不埋点

        const {
          netEventTrack,
          windowLife,
          socketLife,
          socketCreate,
          socketSendEvent,
          socketOnEvent,
        } = TRACE_DATA_TYPE_ENUM;

        let msg = [];

        const reqHeaders = data.reqHeaders || {};

        const projectCode =
          store.state.projectcode ||
          getLocalStoragItem("projectcode") ||
          reqHeaders.ProjectCode; // fix: 兜底reqHeaders内的数据
        const ucloundid =
          store.state.ucloundid || getLocalStoragItem("ucloundid");

        /** 公共的msg字段 */
        const commonMsgData = {
          dataDes: dataDes,
          dataType: DATA_TYPE_MAP[dataDes || ""],
          projectCode: projectCode,
          ucloundid: ucloundid,
          href: window.location.href || "",
        };

        // 1 组装数据
        // 1.1 请求埋点数据
        if (dataDes === netEventTrack) {
          const { sendTs = Date.now(), resultCode } = data || {};
          const receiveTs = Date.now();
          msg.push({
            ...commonMsgData,
            sendTs: sendTs,
            receiveTs: receiveTs,
            timeInterval: receiveTs - sendTs,
            success: resultCode == store.state.rightStatusCode ? 1 : 0, // 数据类型不一样, 不能全等
            ...data,
          });
        }

        // 1.2 socket相关和 window 生命周期数据
        if (
          [
            windowLife,
            socketLife,
            socketCreate,
            socketSendEvent,
            socketOnEvent,
          ].indexOf(dataDes) > -1
        ) {
          msg.push({
            webuuid: window.__webuuid,
            socketUUID: window.__socketUUID,
            ...commonMsgData,
            ts: Date.now(),
            ...data,
          });
        }

        // 1.3 性能指标数据
        if (dataDes == TRACE_DATA_TYPE_ENUM.performance) {
          msg.push({
            webuuid: window.__webuuid,
            ...commonMsgData,
            ts: Date.now(),
            ...data,
          });
        }

        // 1.4 ...
        if (
          [TRACE_DATA_TYPE_ENUM.error, TRACE_DATA_TYPE_ENUM.info, TRACE_DATA_TYPE_ENUM.shuangluTrans].indexOf(
            dataDes
          ) > -1
        ) {
          msg.push({
            ...commonMsgData,
            ...data,
          });
        }


        if (!msg.length) return;
        const browser = Bowser.getParser(window.navigator.userAgent);
        const { name, version } = browser.getBrowser() || {}; // 浏览器名称和版本

        const parameter = {
          timestamp: Math.round(Date.now() / 1000),
          appId: APP_ID,
          appname: APP_NAME,
          platform: `${name} ${version}`,
          appversion: store.state.edition || "",
          ts: moment().format("YYYY-MM-DDTHH:mm:ss.SZ"),
          utcts: moment().utc().format("YYYY-MM-DDTHH:mm:ss.SZ"),
          msg: JSON.stringify(msg),
        };

        // 按字母顺序排序生成qs, qs.stringify 默认会encode, 加签不需要encode的数据
        const querystring1 = decodeURIComponent(
          qs.stringify(parameter, {
            sort: alphabeticalSort,
          })
        );

        // 加签
        // TODO: 后续如果需要新增两个字段 extra1, extra2, 记住加签的时候应该把这两个字段剔除掉, gif请求时再拼接上去, 服务端会透传 (具体联系: 褚工, 方振)
        const sign = sha256withRSA(querystring1, privateKey);

        // 将sign字段塞进parameter中
        parameter.sign = sign;
        // 生成最终带sign字段的qs
        const querystring2 = qs.stringify(parameter, {
          sort: alphabeticalSort,
        });

        // 请求gif, 有响应一张gif图片, 说明埋点成功了
        let img = new Image();
        const url = `${getGifUrl()}?${querystring2}`;
        img.src = url;

        const timer = setTimeout(() => {
          img.src = ''; // 中断请求
          console.log('埋点超时', url);
        }, 3000);

        img.onload = img.onerror = () => clearTimeout(timer);
      } catch (error) {
        console.log("error-Trace: ", error);
      }
    },
    // 回调在 timeout 毫秒过后还没有被调用，那么回调任务将放入事件循环中排队
    { timeout: 3000 }
  );
}

/**
 * 按字母顺序排序
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
function alphabeticalSort(a, b) {
  return a.localeCompare(b);
}

/**
 * 使用sha256withRSA加密字符串 获取签名
 * @link https://www.jianshu.com/p/145eab95322c
 * @link https://blog.csdn.net/weixin_41711709/article/details/112797312
 * @param {string} str 被加密的字符串
 * @param {string} privateKey 加密私钥
 */
function sha256withRSA(str, privateKey) {
  // console.time("加签耗时");
  // 将私钥 转成16进制
  const rsaKey = jsrsasign.KEYUTIL.getKey(privateKey);
  // 采用SHA256withRSA进行加密
  const signature = new jsrsasign.KJUR.crypto.Signature({
    // alg: "SHA256withRSA",
    alg: "SHA1withRSA",
  });
  // 算法初始化
  signature.init(rsaKey);
  // 对str进行加密
  signature.updateString(str);
  // 生成16进制签名
  const sign = signature.sign();
  // console.timeEnd("加签耗时");

  return sign;
}
